/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import _times from "lodash/times";
import moment from "moment";
import MenuItem from "@mui/material/MenuItem";
import ButtonLoading from "../../Button/ButtonLoading";
import * as loginActions from "../../../store/login/actions";
import * as messagesActions from "../../../store/messages/actions";
import EmailCheck from "../../EmailCheck/EmailCheck";
import { getLpByLocation } from "../../../services/landingPagesList";
import IconHeadline from "../Common/IconHeadline/IconHeadline";
import CostsIcon from "../../../assets/icons/serviceOverview/IconCosts";
import ButtonTextTermsOfServicePrivacy from "../Common/ButtonTextTermsOfServicePrivacy/ButtonTextTermsOfServicePrivacy";
import { emailDefault, isPhoneNumberDefault, requiredValidator } from "../../../services/validationRules";
import GradientPaper from "../../GradientComponents/GradientPaper";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import { FormProvider, useForm } from "../../../provider/Form/FormProvider";
import FormLegend from "../../Form/Common/FormLegend/FormLegend";
import Person from "../../Form/Common/Person/Person";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import NumberFieldConfigured from "../../Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import calculateSettlementCheckinStyle from "./calculateSettlementCheckinStyle";
import { Box } from "@mui/material";

type CalculateSettlementCheckinProps = {
  target?: string;
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
};

export default function CalculateSettlementCheckin({
  target = "/servicewelt",
  idPrefix,
  color = "default",
  checkinHeading = "Abfindung berechnen",
}: CalculateSettlementCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [externalErrors, setExternalErrors] = useState({});
  const [emailErrorText, setEmailErrorText] = useState("");

  const queryClient = useQueryClient();
  const createMutation = useMutation(createResource);

  const initialValues = {
    numberOfEmployees: "",
    employedSinceMonth: "",
    employedSinceYear: "",
    grossSalary: "",
    email: "",
    telephone: "",
    gender: "",
    givenName: "",
    familyName: "",
    name: "prelead",
  };

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);
    const { from } = location.state || { from: { pathname: target } };

    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }
    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };

    const settlementData = {
      employedSince: values.employedSinceYear + "-" + values.employedSinceMonth + "-01",
      grossSalary: parseInt(values.grossSalary, 10),
      numberOfEmployees: parseInt(values.numberOfEmployees, 10),
      affiliateCode: localStorage.getItem("gclid"),
      landingPage: landingPage.keyword,
    };

    const registerData = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: "settlement",
        ...settlementData,
      },
    };

    if (!moment(settlementData.employedSince).isBefore(new Date(), "day")) {
      setExternalErrors({
        employedSinceYear: "Das Datum muss in der Vergangenheit liegen.",
        employedSinceMonth: "Das Datum muss in der Vergangenheit liegen.",
      });
      setIsLoading(false);
      return;
    }

    try {
      await loginActions.register(registerData);
      await createMutation.mutateAsync({ uri: "/settlements", data: settlementData });

      await queryClient.invalidateQueries(queryKeys.collection("/customers"));
      navigate(from.pathname + from.search);
    } catch (err) {
      setIsLoading(false);
      messagesActions.addMessage({
        type: "error",
        text: "Bei Ihrer Registrierung ist ein technischer Fehler aufgetreten. Bitte wenden Sie sich an unseren Kundenservice!",
      });
    }
  };

  return (
    <GradientPaper sx={[calculateSettlementCheckinStyle.paper, calculateSettlementCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={calculateSettlementCheckinStyle.scrollToAnchor} />
      <IconHeadline IconImage={CostsIcon} headlineText={checkinHeading} />
      <FormProvider initialValues={initialValues} submitAction={handleSubmit} externalErrors={externalErrors}>
        <CalculateForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </GradientPaper>
  );
}

type CalculateFormProps = {
  isLoading: boolean;
  emailErrorText: string;
  setEmailErrorText: (emailErrorText: string) => void;
};
const CalculateForm = ({ isLoading, emailErrorText, setEmailErrorText }: CalculateFormProps) => {
  const { handleSubmit } = useForm();
  const startYear = 1970;
  const currentYear = moment().year();
  const { values } = useForm();

  return (
    <Box component={"form"} sx={calculateSettlementCheckinStyle.applicationForm} onSubmit={handleSubmit}>
      <Grid container justifyContent={"center"}>
        <Grid item xs={12} sx={calculateSettlementCheckinStyle.gridTopSpaced}>
          <FormLegend>Wie viele Mitarbeiter arbeiten ungefähr in Ihrem Unternehmen?</FormLegend>
        </Grid>
        <Grid item xs={12}>
          <ValidatorSelectField
            label="Anzahl Mitarbeiter"
            name="numberOfEmployees"
            validators={[requiredValidator("Bitte geben Sie die Anzahl Mitarbeiter an")]}
          >
            <MenuItem value={"10"}>0 - 10</MenuItem>
            <MenuItem value={"50"}>11 - 50</MenuItem>
            <MenuItem value={"500"}>51 - 500</MenuItem>
            <MenuItem value={"1000"}>Mehr als 500</MenuItem>
          </ValidatorSelectField>
        </Grid>
        <Grid item xs={12} sx={calculateSettlementCheckinStyle.gridTopSpaced}>
          <FormLegend>Seit wann arbeiten Sie im Unternehmen?</FormLegend>
        </Grid>
        <Grid item xs={12} sm={6} sx={[calculateSettlementCheckinStyle.gridSpace, calculateSettlementCheckinStyle.gridMargin]}>
          <ValidatorSelectField
            label="Monat"
            name="employedSinceMonth"
            validators={[requiredValidator("Bitte geben Sie den Monat an, seitdem Sie im Unternehmen arbeiten.")]}
            MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
          >
            <MenuItem value={"01"}>Januar</MenuItem>
            <MenuItem value={"02"}>Februar</MenuItem>
            <MenuItem value={"03"}>März</MenuItem>
            <MenuItem value={"04"}>April</MenuItem>
            <MenuItem value={"05"}>Mai</MenuItem>
            <MenuItem value={"06"}>Juni</MenuItem>
            <MenuItem value={"07"}>Juli</MenuItem>
            <MenuItem value={"08"}>August</MenuItem>
            <MenuItem value={"09"}>September</MenuItem>
            <MenuItem value={"10"}>Oktober</MenuItem>
            <MenuItem value={"11"}>November</MenuItem>
            <MenuItem value={"12"}>Dezember</MenuItem>
          </ValidatorSelectField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValidatorSelectField
            label="Jahr"
            name="employedSinceYear"
            validators={[requiredValidator("Bitte geben Sie das Jahr an, seitdem Sie im Unternehmen arbeiten.")]}
            MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
          >
            {_times(currentYear - startYear + 1, (i) => {
              const year = currentYear - i;
              return (
                <MenuItem key={i} value={year.toString()}>
                  {year}
                </MenuItem>
              );
            })}
          </ValidatorSelectField>
        </Grid>
        <NumberFieldConfigured caseData={values} fieldPath={"grossSalary"} fieldType={"money"} required />
        <Grid item xs={12} sx={calculateSettlementCheckinStyle.gridTopSpaced}>
          <FormLegend>Wie können wir Ihnen das Ergebnis sofort zukommen lassen?</FormLegend>
        </Grid>
        <Grid item xs={12} sm={6} sx={[calculateSettlementCheckinStyle.gridSpace, calculateSettlementCheckinStyle.gridMargin]}>
          <EmailCheck
            name="email"
            label="Ihre E-Mail-Adresse"
            autoComplete="email"
            emailErrorText={emailErrorText}
            setEmailErrorText={setEmailErrorText}
            validators={[requiredValidator("Bitte geben Sie Ihre E-Mail-Adresse an"), emailDefault]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValidatorTextField
            name="telephone"
            label="Ihre Telefonnummer"
            autoComplete="tel"
            validators={[requiredValidator("Bitte geben Sie Ihre Telefonnummer an"), ...isPhoneNumberDefault]}
          />
        </Grid>
        <Person path="" fieldsAreRequired={true} />
        <Grid xs={12} item sx={calculateSettlementCheckinStyle.gridTopSpaced}>
          <ButtonLoading isLoading={isLoading} fullWidth={true} variant="contained" color="primary" type={"submit"}>
            Kostenlos Ergebnis erhalten
          </ButtonLoading>
          <ButtonTextTermsOfServicePrivacy />
        </Grid>
      </Grid>
    </Box>
  );
};
